import { useAuth } from './useAuth';
import { useIdleTimeout } from './useIdleTimeout';
import { SESSION_CONFIG } from '@/lib/constants';
import { isPinSwitchDeviceEnabled, listVaultEntries, lockDevice } from '@/lib/session-vault';

/**
 * Idle timeout for the signed-in admin app. Called by the two protected route
 * guards (only one is mounted at a time), NOT by AuthProvider: the provider
 * wraps public pages too, and a kiosk tab on /booth-display in a browser that
 * also holds an admin session must never be locked or signed out.
 *
 * On a PIN-fast-switch device with saved sessions, LOCK to the switch screen
 * (keep the session) instead of fully signing out — so the next person
 * re-enters with a PIN rather than a password. Otherwise sign out. The
 * full-page redirect lands on /switch (the active session is still valid, so
 * StandaloneProtectedRoute lets it render).
 *
 * Reception runs one admin account across several tabs and devices, and a
 * forgotten one used to time out and sign the admin out everywhere
 * (Sentry 119210031). Two layers, because they cover different things:
 *
 * - **Other tabs, same browser** share one Supabase session — any sign-out,
 *   even `local`, ends it for all of them. So the idle clock itself is shared
 *   (`SHARED_ACTIVITY_KEY`): it only runs out once EVERY tab has been idle.
 * - **Other devices/browsers** hold their own sessions, which the `local`
 *   scope leaves alone. See `signOutAdmin`.
 */
export function useAdminIdleTimeout() {
  const { session, signOut } = useAuth();

  const handleIdleTimeout = () => {
    if (isPinSwitchDeviceEnabled() && listVaultEntries().length > 0) {
      lockDevice(); // real lock — ProtectedRoute now refuses app routes
      if (window.location.pathname !== '/switch') {
        window.location.assign('/switch');
      }
      return;
    }
    void signOut({ scope: 'local' });
  };

  useIdleTimeout({
    timeout: SESSION_CONFIG.IDLE_TIMEOUT_MS,
    onTimeout: handleIdleTimeout,
    enabled: !!session, // Only active when user is authenticated
    throttle: SESSION_CONFIG.ACTIVITY_THROTTLE_MS,
    sharedActivityKey: SESSION_CONFIG.SHARED_ACTIVITY_KEY,
  });
}
